import type { GetStaticProps } from 'next'
import type { ProjectCard, ProjectScreen } from '@/lib/projects'
import type { ScenePage } from '@/types'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import Hero from '@/components/landing/home/hero'
import Statement from '@/components/landing/home/statement'
import Diagnosis from '@/components/landing/home/diagnosis'
import ServicesIndex from '@/components/landing/home/services-index'
import SelectedWork from '@/components/landing/home/selected-work'
import Process from '@/components/landing/home/process'
import Testimonials from '@/components/landing/home/testimonials'
import Why from '@/components/landing/home/why'
import Pricing from '@/components/landing/home/pricing'
import FaqSection from '@/components/generals/faq-section'
import PreviewCta from '@/components/generals/preview-cta'
import { getFeaturedProjects, getProjectCards, getProjectScreens } from '@/lib/projects'
import { homeFaq } from '@/constants/content'

interface HomeProps {
    featured: ProjectCard[]
    total: number
    screens: ProjectScreen[]
}

const Home: ScenePage<HomeProps> = ({ featured = [], total = 0, screens = [] }) => {
    return (
        <Layout>
            <Seo />

            <Hero screens={screens} />
            <Statement />
            <Diagnosis />
            <ServicesIndex />
            <SelectedWork projects={featured} total={total} />
            <Process />
            <Testimonials />
            <Why />
            <Pricing index='08' />

            <FaqSection
                index='09'
                label='FAQ'
                title={(
                    <>
                        Questions, <em className='type-serif'>answered.</em>
                    </>
                )}
                intro='Practical answers on project scope, content, ownership, timelines and ongoing costs.'
                items={homeFaq}
            />

            <PreviewCta index='10' />
        </Layout>
    )
}

Home.scene = 'paper'

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    try {
        const [featured, all, screens] = await Promise.all([getFeaturedProjects(4), getProjectCards(), getProjectScreens()])

        return { props: { featured, total: all.length, screens }, revalidate: 60 }
    }
    catch (error) {
        console.error('Home page: could not load projects', error)

        return { props: { featured: [], total: 0, screens: [] }, revalidate: 30 }
    }
}

export default Home
